/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useBrandingForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing branding form state and validation.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import type { BrandingConfig } from '../types';

const DEFAULT_BRANDING: BrandingConfig = {
  logo: null,
  logoDark: null,
  mobileLogo: null,
  mobileLogoDark: null,
  favicon: null,
  faviconDark: null,
  primaryColor: '#3B82F6',
  secondaryColor: '#64748B',
  appName: 'Ring2All Platform',
  appDescription: 'Multi-tenant communication platform',
  welcomeMessage: 'Welcome to Ring2All Platform',
  footerText: '© 2026 Ring2All LLC. All rights reserved.',
  loginBackgroundColor: '#ffffff',
  loginFontColor: '#000000',
  loginFormBackground: '#4448476B',
  loginSubmitButtonColor: '#202C2C7D',
  loginAddonsMenuColor: '#24363656',
  loginBackgroundImage: null,
  loginLogo: null,
  disableLoginBackgroundImage: false,
};

export function useBrandingForm(initialConfig?: BrandingConfig) {
  const { t } = useI18n();
  const [formData, setFormData] = useState<BrandingConfig>(
    initialConfig ?? DEFAULT_BRANDING
  );
  const [originalData, setOriginalData] = useState<BrandingConfig>(
    initialConfig ?? DEFAULT_BRANDING
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sync form state when initialConfig changes (e.g., after data loads)
  useEffect(() => {
    if (initialConfig) {
      setFormData(initialConfig);
      setOriginalData(initialConfig);
    }
  }, [initialConfig]);

  const hasChanges = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData]
  );

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  const handleInputChange = useCallback(
    (field: keyof BrandingConfig, value: unknown) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value as BrandingConfig[typeof field],
      }));

      if (errors[field as string]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field as string];
          return next;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.appName?.trim()) {
      nextErrors.appName = t('branding.errors.appNameRequired');
    }

    if (!formData.primaryColor?.trim()) {
      nextErrors.primaryColor = t('branding.errors.primaryColorRequired');
    }

    if (!formData.secondaryColor?.trim()) {
      nextErrors.secondaryColor = t('branding.errors.secondaryColorRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const resetForm = useCallback((config: BrandingConfig) => {
    setFormData(config);
    setOriginalData(config);
    setErrors({});
  }, []);

  return {
    formData,
    originalData,
    errors,
    hasChanges,
    hasErrors,
    handleInputChange,
    validateForm,
    resetForm,
  };
}

